import themeColors, { themeFonts, themeFontsSize, themeFontWeights } from "@GeriMed/styles/variables";
import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration";

import { setPreStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridOffset } from "@insite/mobius/GridContainer";
import { GridItemStyle } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { css } from "styled-components";

// For production, include a set of commonly used widgets in the main bundle
// the rest will be split into their own chunks
const widgets = IS_PRODUCTION
    ? require.context("./Widgets", true, /(Header|Basic|Common|Footer|SignIn)\/.+?\.tsx$/)
    : require.context("./Widgets", true, /\.tsx$/);
// enable hot module reloading for widgets
const onHotWidgetReplace = addWidgetsFromContext(widgets);
if (module.hot) {
    module.hot.accept(widgets.id.toString(), () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

// load all pages. Without this they won't be included in the bundle
const pages = require.context("./Pages", true, /\.tsx$/);
const onHotPageReplace = addPagesFromContext(pages);
if (module.hot) {
    module.hot.accept(pages.id.toString(), () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
}

// load all handlers. They could be loaded individually instead.
const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key));

const store = require.context("./Store", true);
store.keys().forEach(key => store(key));

setPreStyleGuideTheme({
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: [0, 576, 768, 992, 1200],
        maxWidths: [540, 767, 1200, 1200, 1200],
    },
    colors: {
        secondary: {
            main: themeColors.drakBlue,
        },
        primary: {
            main: themeColors.drakYellow,
        },
    },
    typography: {
        fontFamilyImportUrl: "/staticContent/stylesheet.css",
        body: {
            size: themeFontsSize.size16,
            lineHeight: themeFontsSize.size24,
            color: themeColors.neroColor,
            fontFamily: themeFonts.fontFamilyProximaNova,
            css: css`
                padding: 0;
                margin: 0;
                background-color: ${themeColors.lightGray67};
                background-repeat: no-repeat;
            `,
        },
        p: {
            weight: "normal",
            size: themeFontsSize.size16,
            lineHeight: themeFontsSize.size24,
            css: css`
                margin-bottom: 1.25rem;
            `,
        },
        h1: {
            size: themeFontsSize.size30,
            lineHeight: themeFontsSize.size16,
            color: themeColors.colorBlueNew,
            css: css`
                font-weight: ${themeFontWeights.regular};
                margin-top: 0.75em;
                margin-bottom: 0.5em;
            `,
        },
        h2: {
            size: themeFontsSize.size20,
            lineHeight: themeFontsSize.size22,
            color: themeColors.colorBlueNew,
            css: css`
                font-weight: ${themeFontWeights.regular};
                margin-top: 1em;
                margin-bottom: 0.75em;
            `,
        },
        h3: {
            fontFamily: themeFonts.fontFamilyMuseoSans,
            size: themeFontsSize.size17,
            color: themeColors.colorNewBlack,
            css: css`
                font-weight: ${themeFontWeights.fontWeightForBold};
                margin-top: 0;
                margin-bottom: 1em;
            `,
        },
        h4: {
            fontFamily: themeFonts.fontFamilyMuseoSans,
            size: themeFontsSize.size17,
            color: themeColors.colorNewBlack,
            css: css`
                font-weight: ${themeFontWeights.fontWeightForBold};
                margin-top: 0;
                margin-bottom: 1em;
            `,
        },
        h5: {
            fontFamily: themeFonts.fontFamilyMuseoSans,
            size: themeFontsSize.size17,
            color: themeColors.colorNewBlack,
            css: css`
                font-weight: ${themeFontWeights.fontWeightForBold};
                margin-top: 0;
                margin-bottom: 1em;
            `,
        },
        h6: {
            fontFamily: themeFonts.fontFamilyMuseoSans,
            size: themeFontsSize.size17,
            color: themeColors.colorNewBlack,
            css: css`
                font-weight: ${themeFontWeights.fontWeightForBold};
                margin-top: 0;
                margin-bottom: 1em;
            `,
        },
    },
    shadows: {
        1: "box-shadow: 0 0 5px 0 #232323;",
    },
    modal: {
        sizeVariants: {
            small: 500,
            medium: 600,
            large: 800,
        },
    },
    button: {
        primary: {
            shape: "pill",
            mergeCss: true,
            hoverStyle: {
                background: themeColors.brightYellow,
            },
            typographyProps: {
                weight: "normal",
                color: themeColors.colorWhite,
            },
            css: css`
                background-color: ${themeColors.drakYellow};
                background-image: linear-gradient(to bottom, #df8d00 0%, #fa8208 100%);
                border-radius: 4px;
                font-family: "MuseoSans";
                font-weight: ${themeFontWeights.medium};
                font-size: 0.875em;
                padding: 0.8125em;
                display: inline-block;
                text-decoration: none;
                text-transform: uppercase;
                box-shadow: none;
                border: 0;
                text-align: center;
                &:hover,
                &:active {
                    background-color: ${themeColors.brightYellow};
                }
                &:disabled {
                    background-image: none;
                    background-color: ${themeColors.lightGray2};
                    -webkit-box-shadow: inset 0 0 10px 0 ${themeColors.lightGray4};
                    box-shadow: inset 0 0 10px 0 ${themeColors.lightGray4};
                    color: ${themeColors.colorDisabledNew};
                    cursor: default;
                    opacity: 1;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
        secondary: {
            shape: "pill",
            mergeCss: true,
            typographyProps: {
                weight: "normal",
                color: themeColors.colorWhite,
            },
            css: css`
                background-color: ${themeColors.colorTertiary};
                border-radius: 4px;
                font-family: "MuseoSans";
                font-weight: ${themeFontWeights.medium};
                font-size: 0.875em;
                padding: 0.8125em;
                display: inline-block;
                text-transform: uppercase;
                text-decoration: none;
                box-shadow: none;
                border: 0;
                text-align: center;
                &:hover,
                &:active {
                    color: ${themeColors.colorWhite} !important;
                    background-color: ${themeColors.secondarybtn};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
        tertiary: {
            shape: "pill",
            mergeCss: true,
            typographyProps: {
                weight: "normal",
                color: themeColors.colorWhite,
            },
            css: css`
                background-color: ${themeColors.colorBlue2};
                border-radius: 4px;
                font-family: "MuseoSans";
                font-weight: ${themeFontWeights.medium};
                font-size: 0.875em;
                padding: 0.8125em;
                display: inline-block;
                text-transform: uppercase;
                text-decoration: none;
                box-shadow: none;
                border: 0;
                text-align: center;
                &:hover,
                &:active {
                    background-color: #09f;
                }
            `,
        },
    },
    header: {
        isFullWidth: true,
    },
    footer: {
        isFullWidth: true,
    },
    link: {
        defaultProps: {
            color: themeColors.lightBlue,
        },
    },
    radio: {
        defaultProps: {
            mergeCss: true,
            css: css`
                input {
                    & + label {
                        &::before {
                            content: "";
                            box-sizing: border-box;
                            display: inline-block;
                            height: 13px;
                            width: 13px;
                            border-radius: 50%;
                            border: 1px solid ${themeColors.colorRedioNew};
                            margin-right: 10px;
                        }
                    }
                    &:checked {
                        + label::after {
                            content: "";
                            display: block;
                            height: 9px;
                            width: 9px;
                            border-radius: 50%;
                            background: ${themeColors.colorRedioNew};
                            position: absolute;
                            left: 2px;
                            top: calc(50% - 5px);
                        }
                        &:not(:disabled) {
                            & + label::before {
                                background: ${themeColors.colorWhite};
                                border-color: ${themeColors.colorRedioNew};
                            }
                        }
                    }
                    &:focus {
                        + label::before {
                            box-shadow: none;
                        }
                    }
                    &:disabled {
                        + label::before {
                            background: ${themeColors.colorWhite};
                            border: 2px solid ${themeColors.lightGray1} !important;
                        }
                    }
                }
            `,
        },
    },
    checkbox: {
        defaultProps: {
            color: themeColors.colorGray,
            indeterminateColor: themeColors.colorGray,
            mergeCss: true,
            css: css`
                span[role="checkbox"] {
                    border: 1px solid ${themeColors.colorGray};
                    width: 16px;
                    height: 16px;
                    justify-content: center;
                    margin-top: 0 !important;
                    flex-shrink: 0;

                    :focus {
                        box-shadow: none;
                    }

                    + label {
                        font-size: 0.9375em;
                        color: ${themeColors.lightGray1};
                        cursor: pointer;
                        display: inline-block;
                        font-weight: ${themeFontWeights.medium};
                    }
                }
                span[role="checkbox"][aria-checked="true"]:not([aria-disabled="true"]),
                span[role="checkbox"][aria-checked="mixed"]:not([aria-disabled="true"]) {
                    background: ${themeColors.colorRedioNew};
                    border: 1px solid ${themeColors.colorGray};
                    color: ${themeColors.colorWhite};
                    height: 16px;
                    width: 16px;
                }
            `,
        },
    },
    textField: {
        defaultProps:  {
            mergeCss: true,
            cssOverrides: {
                formField: css`
                    select:not(.react-datetime-picker__inputGroup__input) {
                        background-color: ${themeColors.lightGray4};
                        border-radius: 4px;
                        border: none;
                        &:hover {
                            background-color: ${themeColors.lightGray19};;
                            border-color: ${themeColors.lightGray11};
                            box-shadow: 0 0 5px 0 rgba(0, 0, 0, .4);
                        }

                        &:focus {
                            border-color: ${themeColors.lightGray3};;
                            background-color: ${themeColors.colorWhite};
                            box-shadow: 0 0 5px 0 rgba(0, 0, 0, .25);
                            border: none;
                            border-radius: 4px;
                        }

                        &:disabled {
                            color: ${themeColors.lightGray10};
                            background-color: ${themeColors.lightGray};;
                        }
                    }
                `
            }
        }
    },
    select: {
        defaultProps: {
            mergeCss: true,
            backgroundColor: themeColors.lightGray4,
        }
    },
    breadcrumbs: {
        defaultProps: {
            typographyProps: {
                size: themeFontsSize.size12,
                color: themeColors.lightGray1,
                css: css`
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css`
                                    font-size: ${themeFontsSize.size12};
                                    line-height: ${themeFontsSize.size20};
                                `,
                            ],
                            "min",
                        )}
                `,
            },
        },
    },
    globalStyle: {
        css: css`
            body {
                background: ${themeColors.lightGray67};
                font-size: ${themeFontsSize.size16};
                line-height: 24px;
                color: ${themeColors.neroColor};
                font-family: ${themeFonts.fontFamilyProximaNova};
                padding: 0;
                margin: 0;
                background-repeat: no-repeat;
            }
            [data-test-selector="page_HomePage"] main{
                font-family: ${themeFonts.fontFamilyPoppinsMedium};
                .btn-tertiary span,
                .add-to-cart-wrap button,
                .btn.view-deatils{
                    font-family: ${themeFonts.fontFamilyPoppinsMedium};
                }
                .qty-wrap, .price-wrap{
                    text-align: left;
                }
            }
            [data-test-selector="signIn_forgotPassword"]{
                font-family: ${themeFonts.fontFamilyLexendDeca};
            }
            header,
            main {
                background: ${themeColors.colorWhite};
            }
            h1[data-test-selector="PageTitle"] {
                font-family: inherit;
                font-size: ${themeFontsSize.size30};
                font-weight: ${themeFontWeights.regular};
                line-height: 1em;
                margin-top: 0.75em;
                margin-bottom: 0.5em;
            }

            @media print {
                header {
                    display: block !important;
                    .secondary-nav-wrap {
                        display: none !important;
                    }
                }
                .breadcrumb-wrap,
                .budget-table-wrap,
                .cart-wrap,
                .btn.btn-blue {
                    display: none;
                }
            }

            .align-content-center {
                display: block;
                margin: 0 auto;
            }

            .my-16{
                margin: 16px 0;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            input {
                                font-size: ${themeFontsSize.size16};
                            }
                        `,
                    ],
                    "max",
                )}

            input[type="text"],
            input[type="password"],
            input[type="number"],
            input[type="tel"],
            select {
                -webkit-appearance: none;
                appearance: none;
                background-color: ${themeColors.colorWhite};
                font-family: inherit;
                border-radius: 4px;
                border: 1px solid #ccc;
                color: rgba(0, 0, 0, 0.75);
                display: block;
                font-size: 0.875rem;
                margin: 0;
                padding: 0.5rem;
                height: 2.3125rem;
                box-sizing: border-box;
                transition: box-shadow 0.45s, border-color 0.45s ease-in-out;
                width: 100%;
                background-image: none !important;
                &:focus {
                    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25);
                    background-color: #fff;
                    border: 1px solid #bbb;
                }
            }
            .email-text-field{
                border: none !Important;
                border-radius: 10px !Important;
                font-family: ${themeFonts.fontFamilyLexendDeca} !important;
            }
            select:disabled {
                color: ${themeColors.lightGray10};
            }

            select:disabled {
                background-color: ${themeColors.lightGray};
            }

            .error {
                color: ${themeColors.errorRed};
                font-size: ${themeFontsSize.size15};
                font-weight: ${themeFontWeights.semiBold};
            }

            .modal-close-btn {
                background-color: transparent;
                span {
                    font-size: 2em;
                    color: ${themeColors.lightGray9}!important;
                }
                &:hover {
                    background-color: transparent;
                }
            }
            .contactus-page-wrapper {
                padding: 0 1.25rem;
                .contactus-email {
                    span[class*="TypographyStyle"] {
                        line-height: ${themeFontsSize.size24};
                        font-size: ${themeFontsSize.size16};
                        font-weight: ${themeFontWeights.fontWeightnormal};
                        font-family: ${themeFonts.fontFamilyProximaNova};
                    }
                }
            }
            .custom-wrapper {
                padding: 0 1.25rem;
            }
            .custom-box-wrapper {
                padding: 0 0.9375rem;
            }
            .custom-box-top-space {
                padding-top: 0.563rem;
            }
            .solar-section-container,
            .massbid-section-container {
                p {
                    line-height: 26px;
                    font-weight: ${themeFontWeights.regular};
                }
                a {
                    font-family: ${themeFonts.fontFamilyProximaNova};
                    text-decoration: underline;
                    p {
                        font-family: ${themeFonts.fontFamilyProximaNova};
                    }
                    span {
                        font-size: ${themeFontsSize.size16};
                        font-weight: ${themeFontWeights.regular};
                        font-family: ${themeFonts.fontFamilyProximaNova};
                    }
                }
            }
            .form-field-error {
                padding: 0.25em 1em;
            }
            .custom-form-field {
                .form-field-error {
                    display: none;
                }
            }
            // Authentication user
            .guestUserSection {
                .hideForGuest {
                    display: none;
                }
            }

            //custom table
            .custom-gerimed-table {
                width: 100%;
                display: table;
                max-width: 1000px;
                margin: 0 auto;
                border: 1px solid ${themeColors.lightGray};
                &:last-child {
                    margin: 0 auto;
                }
            }
            .gerimed-custom-table-row {
                display: table-row;
                &:nth-child(odd) {
                    background: ${themeColors.lightGray20};
                    color: ${themeColors.colorRaisinBlack};
                    font-weight: ${themeFontWeights.bold};
                }
            }
            .cell-heading {
                font-weight: ${themeFontWeights.bold};
            }
            .cell-hide {
                display: none;
            }
            .gerimed-image-cell {
                width: 25%;
                text-align: center;
            }
            .gerimed-custom-table-cell {
                display: table-cell;
                font-family: ${themeFonts.fontFamilyProximaNova};
                font-size: ${themeFontsSize.size16};
                line-height: 18px;
                color: ${themeColors.colorRaisinBlack};
                padding: 0.5625rem 0.625rem;
                vertical-align: middle;
                p {
                    margin-bottom: 0;
                    line-height: 26px;
                }
            }
            .gerimed-custom-table-cell-spacing {
                padding: 31px 0;
            }

            //about
            .custom-paragraph {
                text-align: justify;
                font-size: ${themeFontsSize.size16};
                font-family: ${themeFonts.fontFamilyArial};
            }
            .why-gerimed-table {
                width: 100%;
                display: table;
                max-width: 800px;
                margin: 0 auto;
                &:last-child {
                    margin: 0 auto;
                }
            }
            .gerimed-table-container {
                display: block;
                border: 1px solid ${themeColors.lightGray};
                margin-bottom: 20px;
            }
            .gerimed-table-row {
                display: table-row;
                &:nth-child(odd) {
                    background: ${themeColors.lightGray20};
                    color: ${themeColors.colorDarkMidnightBlue};
                    font-weight: ${themeFontWeights.bold};
                }
            }
            .gerimed-table-cell {
                display: table-cell;
                font-family: ${themeFonts.fontFamilyArial};
                font-size: ${themeFontsSize.size16};
                line-height: 18px;
                color: ${themeColors.colorDarkMidnightBlue};
                padding: 0.5625rem 0.625rem;
            }
            .gerimed-table-cell-content {
                color: ${themeColors.colorRaisinBlack} !important;
                a {
                    font-family: ${themeFonts.fontFamilyArial};
                    font-size: ${themeFontsSize.size16};
                }
            }
            //returnpolicy
            .return-policy-wrapper {
                .wrapper-head {
                    font-size: ${themeFontsSize.size24};
                    line-height: ${themeFontsSize.size38};
                    color: #252525;
                    padding-top: ${themeFontsSize.size30};
                }
                p {
                    font-family: ${themeFonts.fontFamilyProximaNova};
                    font-weight: ${themeFontWeights.regular};
                    font-size: ${themeFontsSize.size16};
                    line-height: ${themeFontsSize.size26};
                }
                .table-responsive {
                    margin-bottom: 1.25rem;
                }
            }
            // Home Page
            .home-page-link img {
                margin-top: -9px;
                width: 150px;
            }
            .wrapper.who-we-serve-title{
                padding: 0 22px;
            }
            div .offer-info h4 {
                margin-bottom: 6px !important;
                font-family: ${themeFonts.fontFamilyTTFors};
            }
            .current-promotion-container .arrow-icon {
                display: inline;
            }
            
            .current-promotion-container .arrow-icon img {
                width: ${themeFontsSize.size16};
                top: 4px; 
                position: relative; 
                left: 6px;
            }
            .who-we-serve-row2 .who-we-served-small-img p {
                top: -56px;
            }
            
            .whoServedSection .img-wrapper {
                padding-bottom: 0;
            }
            .img-wrapper a[class*="StyledClickable--"] {
                display: block;
            }
            .who-we-served-container div[class*="GridItemStyle--"]:nth-of-type(1){
                position: relative;
            }
            p.who-we-serve-big-img-text {
                position: absolute;
                top: 16px;
            }
            .who-we-served-small-img {
                position: relative;
                p{
                    font-size: 12px;
                }
            } 
            main div[class*="GridWrapper"].who-we-served-container,
            main div[class*="GridWrapper"].wrapper.who-we-serve-title,
            main div[class*="GridWrapper"].four-section-container,
            main div[class*="GridWrapper"].current-promotion-container,
            main div[class*="GridWrapper"].stay-info-container,
            .wrapper.who-we-served-container,
            .stayed-info-title {
                padding: 0;
                .offer-info h4{
                    font-family: ${themeFonts.fontFamilyPoppinsSemiBold};
                }
                .text-sub-heading{
                    font-family: ${themeFonts.fontFamilyTTFors};
                }
            }
            .feature-product-container .product-carousel-wrapper > div > div {
                max-width: 100%; 
                flex-basis: 100%;
            }
            main div[class*="GridWrapper"].become-a-customer-container{
                background-color: ${themeColors.colorBluish};
                padding-bottom: 50px;                
            }
            @media only screen and (min-width: 1024px) {
                main div[class*="GridWrapper"].become-a-customer-container{
                    background-color: ${themeColors.colorBluish};
                    padding: 20px 100px;
                }
                .wrapper.who-we-serve-title{
                    padding: 0 0;
                }
                .who-we-served-container .offer-info{
                    padding: 0 0;
                }
                .feature-product-container [data-test-selector="productImage"] img{
                    width: 100%;
                    height: 160px;
                }
                .who-we-served-small-img p{
                    font-size: ${themeFontsSize.size16};
                }
            }
            @media only screen and (min-width: 360px) and (max-width: 1024px) {
                .current-promotion-container .slideshow-wrapper .slide-wrapper {
                    background-size: contain; background-position: center center;
                }
            }
            .sub-menu-right {
                span[class*="TypographyStyle"]{
                    font-size: ${themeFontsSize.size13};
                    font-family: ${themeFonts.fontFamilyLexendDeca};
                }
                div[class*="GridItemStyle"]{
                    max-width: 100%;
                    flex-basis: 100%;
                    display: none;
                }
            } 
            .who-we-served-container h4.who-we-serve-title,
            .offer-info h4{
                font-family: ${themeFonts.fontFamilyPoppinsSemiBold};
            }
            @media (min-width: 1024px){
                .sub-menu-right div[class*="GridItemStyle"]{
                    display: flex;
                    align-items: center;
                }
            }
            main div[class*="GridWrapper--"].four-section-container{
                margin-bottom: ${themeFontsSize.size40};
            }
            .text-heading{
                margin-bottom: 0;
            }
            .text-sub-heading{
                color: ${themeColors.drakBlue} !important;
                font-family: ${themeFonts.fontFamilyPoppinsLight};
            }
            .current-promotion-container,
            .stayInformedSection {
                background: ${themeColors.colorBluish};
            }
            div .text-sub-heading {
                font-size: ${themeFontsSize.size18};
                text-align: left; 
                display: block;
                span{
                    font-size: ${themeFontsSize.size18};
                }
            }
            .circle-right-arrow{
                left: 0;
                height: ${themeFontsSize.size28};
                border-radius: ${themeFontsSize.size16};
                background-color: ${themeColors.colorWhite};
                border: 1px solid #bababa;
                font-size: ${themeFontsSize.size20};
                width: ${themeFontsSize.size28};
                top: 19px;
                position: relative;
            }
            // Testimonial
            .testimonial-card {
                background: ${themeColors.colorWhite};
                padding: ${themeFontsSize.size20};
                width: 370px;
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                text-align: center;
                margin-bottom: 10px !important;

            }
            .testimonial-text {
                font-size: ${themeFontsSize.size12};
                color: ${themeColors.drakGray};
                margin-bottom: 15px;
                text-align: left;
                font-family: ${themeFonts.fontFamilyTTFors};
            }
            .stars {
                display: flex;
                justify-content: left;
                gap: 5px;
            }
            .star {
                width: ${themeFontsSize.size20};
                height: ${themeFontsSize.size20};
                background-color: ${themeColors.lightGray8};
                clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
            }
            .star.filled {
                background-color: #0C5393;
            }
            .profile {
                display: flex;
                align-items: center;
                justify-content: left;
                gap: ${themeFontsSize.size10};
                margin-top: ${themeFontsSize.size10};
            }
            .profile img {
                border-radius: 50%;
                padding: 5px 0;
            }
            .profile-info {
                text-align: left;
            }
            .profile-info h4 {
                margin: 0;
                font-size: ${themeFontsSize.size16};
                color: ${themeColors.colorBlack};
                font-family: ${themeFonts.fontFamilyPoppinsSemiBold};
            }
            .profile-info p {
                margin: 0;
                font-size: ${themeFontsSize.size14};
                color: #777;
                font-family: ${themeFonts.fontFamilyLexendDeca};
            }
            //Stay Info Container
            .stay-info-container{
                .stay-img-1, .stay-img-2, .stay-img-3{
                    padding: 5px;
                    object-fit: cover;
                }
                .img.stay-img-1{
                    height: 456px;
                }
                .img.stay-img-2, 
                .img.stay-img-3{
                    height: auto;
                    width: 100%;
                }
                @media (min-width: 1024px){
                    .img.stay-img-2, 
                    .img.stay-img-3{
                        height: 225px;
                        width: 221px;
                    }
                }
                .img-wrapper{
                    padding-bottom: 0;
                }
            }
            @media (min-width: 1024px) and (max-width: 1440px) {
                .stay-info-container-details{
                    margin: 44px 50px;
                }
            }
            .stay-info-container-details {
                max-width: 650px;
                margin: 0;
                width: 100%;
                .item {
                    margin-bottom: ${themeFontsSize.size20};
                }
                
                h2 {
                    font-size: ${themeFontsSize.size18};
                    margin-bottom: 5px;
                    display: flex;
                    align-items: center;
                    color: ${themeColors.colorBlack};
                    font-family: ${themeFonts.fontFamilyPoppinsSemiBold};
                }
                
                .icon {
                    margin-left: 5px;
                    color: #004080;
                    font-size: ${themeFontsSize.size14};
                }
                
                p {
                    font-size: ${themeFontsSize.size16};
                    color: ${themeColors.colorBlack};
                    margin-bottom: 5px;
                    font-family: ${themeFonts.fontFamilyTTFors};
                    line-height: 22.4px;
                }
                
                a {
                    font-size: ${themeFontsSize.size14};
                    color: #0073e6;
                    text-decoration: none;
                    font-weight: bold;
                }
                
                a:hover {
                    text-decoration: underline;
                }
                span{
                    font-family: ${themeFonts.fontFamilyLexendDeca};
                }
                .blue-arrow{
                    width: ${themeFontsSize.size20};
                    height: ${themeFontsSize.size20};
                    top: 3px;
                }
            }
            .stayed-info-title{
                .offer-info{
                    padding: 70px 0 8px;
                    h4{
                        line-height: ${themeFontsSize.size30};
                    }
                }
            }
            main div[class*="GridWrapper--"].stay-info-container{
                padding-bottom: 100px;
                .stay-info-img-row{
                    gap: 50px;
                    flex-wrap: initial;
                }
            }
            table {
                tr {
                    th,
                    td {
                        padding: 0.5625rem 0.625rem;
                        font-size: 0.875rem;
                        color: ${themeColors.colorRaisinBlack};
                        text-align: left;
                    }
                    &.even,
                    &:nth-of-type(even) {
                        background: ${themeColors.colorWhite};
                    }
                    &.odd,
                    &:nth-of-type(odd) {
                        background-color: ${themeColors.lightGray20};
                    }
                }
            }
            input:not(.react-datetime-picker__inputGroup__input):focus{
                box-shadow: none !important;
            }
            .submitted-message{
                font-family: ${themeFonts.fontFamilyPoppinsRegular};
            }
            table thead tr th,
            table tbody tr th,
            table tbody tr td,
            table tr td {
                display: table-cell;
                line-height: 1.125rem;
            }

            .btn {
                cursor: pointer;
                font-family: inherit;
                line-height: 0.8125em;
                word-break: keep-all;
                padding: 0 21px;
                background: #df8d00;
                color: ${themeColors.colorWhite};
                border-radius: 4px;
                font-family: "MuseoSans";
                font-weight: ${themeFontWeights.medium};
                font-size: 0.875em;
                padding: 0.8125em;
                display: inline-block;
                text-decoration: none;
                text-transform: uppercase;
                color: #fff !important;
                box-shadow: none;
                text-align: center;
                margin-top: 10px;
            }

            .btn-primary {
                background-color: ${themeColors.drakYellow};
                &:hover,
                &:active {
                    color: #ffffff !important;
                    background-color: ${themeColors.brightYellow};
                    text-decoration: none;
                }

                &:hover span {
                    color: #ffffff !important;
                }
                span {
                    font-size: ${themeFontsSize.size13};
                    color: #ffffff !important;

                    &:hover {
                        text-decoration: none;
                    }
                }
            }

            .btn-tertiary {
                font-weight: ${themeFontWeights.regular};
                font-size: ${themeFontsSize.size14};
                background-color: ${themeColors.colorTertiary};
                &:hover {
                    background-color: ${themeColors.colorTertiaryNew};
                }
                span {
                    color: #fff !important;
                }
                &.btn-blue {
                    background-color: ${themeColors.colorBlue2};
                    min-height: 40px;
                    margin-top: 0;
                    span {
                        vertical-align: sub;
                    }
                    &:hover {
                        background-color: #09f;

                        span {
                            color: #fff !important;
                        }
                    }
                }
            }

            .primary-txt-fntcolor {
                color: #df8d00 !important;
                font-size: 14px;
                margin-bottom: 5px;

                &.d-block{
                    display: block;
                }
            }

            a {
                p {
                    color: ${themeColors.lightBlue} !important;
                }
            }

            a:hover,
            a:hover span {
                color: #1c3e95 !important;
            }

            *:focus {
                outline: none !important;
            }

            label {
                font-size: ${themeFontsSize.size15};
                color: ${themeColors.lightGray1};
                cursor: pointer;
                display: inline-block;
                font-weight: ${themeFontWeights.medium};
            }

            body {
                background-color: ${themeColors.lightGray67};
            }
            [class*="StyledTypography"] {
                font-size: 14px;
            }
            main {
                width: 100%;
                min-height: 500px;

                &[data-test-selector="homePage"],
                &[data-test-selector="homePage"] [class*="GridWrapper"] {
                    @media screen and (min-width: 1200px) {
                        min-width: 100%;
                        padding: 0;
                    }
                }
                &[data-test-selector="homePage"] {
                    padding: 0 !important;
                }

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            &[class*="PageStyle"]{
                                padding: 0 15px 24px;
                            }
                        `,
                    ],
                    "min",
                )}
            }

            main[data-test-selector="homePage"] .service-section {
                background: #f2f7f9;
                padding-bottom: 30px;

                > div {
                    margin: 0 auto;
                    max-width: 1024px;
                    width: 100%;
                }
            }
            .logged-in-quick-links{
                max-width: 400px;
                margin-top: ${themeFontsSize.size20};
                .button-container {
                    display: flex; 
                    flex-direction: column; 
                    gap: 15px;
                    align-items: center;
                }
                
                .menu-list {
                    display: flex; 
                    align-items: center; 
                    justify-content: space-between; 
                    width: 260px; 
                    padding: 0 20px; 
                    border: 2px solid ${themeColors.lightGray};
                    border-radius: 25px; 
                    background: ${themeColors.colorWhite};
                    font-size: 16px; 
                    cursor: pointer; 
                    text-decoration: none; 
                    color: black; 
                    font-weight: bold; 
                    height: 50px;
                    img {
                        width: ${themeFontsSize.size40};
                    }
                }
                .button-container a> span {
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                    align-items: center;
                    color: ${themeColors.colorBlack};
                    font-family: ${themeFonts.fontFamilyPoppinsLight};
                }
            
            }
            @media only screen and (max-width: 1023px) {
                .logged-in-quick-links .button-container {
                    margin-bottom: 40px;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            main[data-test-selector="homePage"] {
                                .service-section {
                                    padding-top: 30px;
                                }
                            }
                        `,
                    ],
                    "max",
                )}

            .user-error {
                line-height: 1.5em;
                font-weight: ${themeFontWeights.semiBold};
                display: block;
                clear: both;
                font-size: ${themeFontsSize.size14};
                margin-bottom: 0.25em;
                border: 1px solid ${themeColors.darkRed};
                padding: 0.25em 1em;
                background: ${themeColors.darkRed};
                color: ${themeColors.colorWhite};
            }

            .mb-10 {
                margin-bottom: 10px;
            }
            .mb-20 {
                margin-bottom: 20px;
            }
            .mt-10 {
                margin-top: 10px;
            }
            .mt-20 {
                margin-top: 20px;
            }      

            .hide{
                display:none;

                &.active {
                    display: block;
                }
            }

            .m-0 {
                margin: 0 !important;
            }
            .menu-caret > a::after,
            .caret::after {
                content: "";
                border-top: 5px solid transparent;
                border-bottom: 5px solid transparent;
                border-left: 5px solid ${themeColors.drakBlue};
                display: inline-block;
                vertical-align: middle;
                margin-left: 4px;
            }
            .stay-info-container{
                div[class*="GridItemStyle--"]:nth-of-type(3){
                    align-items: center;
                }
            }
            footer {
                .list-wrapper {
                    text-align: center;
                    @media (min-width: 1024px){
                        text-align: right;
                        white-space: nowrap;
                    }
                    text-transform: capitalize;
                    padding: 0;
                    @media screen and (max-width: 640px) {
                        display: block;
                        flex-direction: column;

                        .links-wrapper {
                            padding-right: 0;
                            margin: 0 0 20px 0;
                        }
                    }
                    
                }
                @media (min-width: 1024px) and (max-width: 1440px) {
                    .list-wrapper{
                        display: flex;
                        align-items: inherit;
                        justify-content: right;
                    }
                }
                .links-wrapper {
                    padding-right: 16px;
                    margin: 0 16px 20px 0;
                    a.list-link,
                    a.list-link span {
                        font-size: ${themeFontsSize.size24};
                        // line-height: ${themeFontsSize.size26};
                        font-family: ${themeFonts.fontFamilyLexendDeca};

                        &:hover {
                            color: ${themeColors.colorWhite};
                        }
                    }
                }
                .footer-links a:hover,
                .footer-links a:focus {
                    text-decoration: underline;
                    text-decoration-color: ${themeColors.lightBlue};
                    color: ${themeColors.heavyBlue};
                }
                .footer-container [class*="GridItemStyle"]{
                    background: ${themeColors.drakBlue};
                }
                .description-text p {
                    cursor: pointer;
                    margin-bottom: ${themeFontsSize.size10};
                    font-size: ${themeFontsSize.size12};
                    font-weight: ${themeFontWeights.medium};
                    line-height: 19px;
                    font-family: ${themeFonts.fontFamilyLexendDeca};
                    line-height: 22.4px;
                }
                .social-links {
                    display: block;
                    text-align: center;
                    padding: 0;
                }

                .social-links img {
                    height: 50px;
                    width: 50px;
                }

                .social-links a:hover {
                    text-decoration: none;
                }

                .email-button span {
                    font-size: ${themeFontsSize.size12};
                }

                .bottom-section .links-wrapper a.list-link span {
                    color: ${themeColors.colorWhite} !important;
                    padding: ${themeFontsSize.size12};
                    font-family: ${themeFonts.fontFamilyPoppinsRegular};
                    font-size: ${themeFontsSize.size12};
                }
                @media (min-width: 1024px) and (max-width: 1440px) {
                    .bottom-section .links-wrapper a.list-link span {
                        padding: 0;
                        font-family: ${themeFonts.fontFamilyLexendDeca};
                        font-size: ${themeFontsSize.size12};
                    }
                }
                .bottom-section{
                    padding: 0 0;
                    .wrapper{
                        font-family: ${themeFonts.fontFamilyLexendDeca};
                    }
                }
                @media (min-width: 1024px) and (max-width: 1440px) {
                    .bottom-section{
                        padding: 0 80px;
                    }
                    .bottom-section .wrapper{
                        padding: 0 80px;
                    }
                }
                .links-wrapper:not(:last-child) {
                    border-right: 1px solid #6672a0;
                }
                .footer-social-icons img {
                    width: ${themeFontsSize.size22};
                    height: ${themeFontsSize.size22};
                }
                .footer-social-icons{
                    text-align: center;
                    margin-top: 0 !important;
                }
                @media (min-width: 1024px){
                    .footer-social-icons{
                        text-align: right;
                        margin-top: 92px !important;
                    }
                }
                .copyright-section-2{
                    gap: 20px;
                    justify-content: end;
                    display: grid;
                }
                @media (min-width: 1024px){
                    .copyright-section-2{
                        display: inline-flex;
                    }
                }
                .copyright-section-2 img {
                    width: 20px;
                    top: 6px;
                    position: relative;
                    right: 2px;
                }
                .copyright-section-2 span,
                .copyright-section-2 div{
                    font-size: ${themeFontsSize.size12};
                    font-family: ${themeFonts.fontFamilyLexendDeca};
                }
            } 
            .copyright-section-2 a {
                font-size: ${themeFontsSize.size12};
                color: ${themeColors.colorWhite}; 
                &:hover {
                color: ${themeColors.colorWhite};
                }
            }
            
            footer .copyright-section-2 span {
                font-size: ${themeFontsSize.size12};
                color: ${themeColors.colorWhite} !important; 
                display: inline;
            }
            
            footer .links-wrapper a.list-link {
                line-height: 0;
            }
            @media (max-width: 991px) {
                .email-grid-item {
                    display: flex;
                }
            }
            .product-slider {
                .prev-btn-wrap,
                .next-btn-wrap {
                    button svg {
                        color: ${themeColors.colorWhite};
                    }
                } 
                &.slider-no-width-border{
                    border: none;
                    max-width: 100%;
    
                    @media screen and (max-width: 1080px) {
                        margin: 0 ;
                    }
                }
            }
            .logged-in {
                .login-wrap {
                    padding: 10px 18px;
                }
            }
            .guest {
                .sign-out-wrapper {
                    display: none;
                }
            }
            // who we server 
            .img.who-we-serve-img {
                padding: 3px;
                height: auto;
            }
            .who-we-served-container .img-wrapper{
                padding-bottom: 0;
                height: auto;
            }
            .img.who-we-serve-big-img {
                padding: 4px;
                height: auto;
            }
            @media (min-width: 1024px){
                .who-we-served-container .img-wrapper{
                    height: 184px;
                }
                .img.who-we-serve-big-img {
                    padding: 4px;
                    height: 367px;
                }
                .img.who-we-serve-img {
                    padding: 4px;
                    height: 194px;
                    width: 100%;
                }
                .who-we-serve-row2{
                    margin-top: 10px;
                }
            }
            
            .wrapper.who-we-serve-img{
                height: 0;
            }
            .who-we-serve-img p{
                top: -62px;
                position: relative;
                text-align: center;
                color: #ffffff;
                width: 100%;
                margin: 0 auto;
                font-size: ${themeFontsSize.size12};
            }
            @media (min-width: 1024px) {
                .who-we-serve-img p{
                    width: 100%;
                    font-size: 19px;
                    padding: 0px 16px;
                }
            }
            p.who-we-serve-big-img-text{
                // top: -168px;
                width: 100%;
                text-align: right;
                font-size: ${themeFontsSize.size22};
                padding-right: 22px;
                position: absolute;
                top: 16px;
            }
            .learn-more-btn{
                background-color: #f7941d;
                text-transform: capitalize;
                border: none;
                font-family: ${themeFonts.fontFamilyLexendDeca};
            }
            .border-none{
                border: none;
                background-color: #f7941d;
                margin-left: ${themeFontsSize.size20};
            }
            .who-we-serve-container {
                display: flex;
                align-items: center;
                gap: 8px;
                .icon {
                    width: 60px;
                    height: 60px;
                }
                .text {
                    display: flex;
                    flex-direction: column;
                }
                .title {
                    font-size: ${themeFontsSize.size16};
                    font-family: ${themeFonts.fontFamilyLexendDeca};
                }
                .subtitle {
                    font-size: 13px;
                    color: gray;
                    font-family: ${themeFonts.fontFamilyTTFors};
                }
            }
            .who-we-served-small-img > div > div {
                position: relative;
            } 
            .who-we-served-small-img p {
                position: absolute;
                width: calc(100% - 15px);
                background-color: white;
                padding: 2px 12px;
                background: linear-gradient(to right, rgba(255, 255, 255, 1) 48%, rgba(255, 255, 255, 0) 100%);
                top: -50px;
                font-family: ${themeFonts.fontFamilyLexendDeca};
            }
            .four-section-container{
                margin-top: ${themeFontsSize.size30};
            }
            .cascading-menu [class*="MenuStyle"] li a span {
                font-family: ${themeFonts.fontFamilyPoppinsMedium} !important; 
                text-transform: capitalize !important;
            }
            .logged-in .home-page-link img {
                margin-top: -20px;
            }
            // Featured Product Container

            .feature-product-container{
                [data-test-selector="slides"]{
                    flex-wrap: wrap;
                    gap: ${themeFontsSize.size12};
                    margin-bottom: 5rem;
                }
                .slide-list{
                    width: calc(25% - 10px);
                    max-width: calc(25% - 10px);
                    box-sizing: border-box;
                    background: ${themeColors.colorWhite};
                    padding: 10px 10px 0 10px;
                    text-align: center;
                    border: 1px solid #efefef;
                }
                [data-test-selector="carousel_slide"]{
                    padding: 0;
                }
                [data-test-selector="GridOffset--"]{
                    display: block;
                }
                .product-desc span{
                    text-align: left;
                    color: black;
                    font-size: ${themeFontsSize.size14};
                    font-weight: 600;
                    font-family: ${themeFonts.fontFamilyPoppinsSemiBold};
                }
                @media (min-width: 1200px) {
                    .carousel-container {
                        max-width: 100%;
                    }
                }
                [data-test-selector="GridOffset--"]{
                    display: block;
                    margin-bottom: 80px;
                }
                
                .product-num-brand > div {
                    display: block !important;
                }
                [class*="GridItemStyle--"].product-num-wrap, 
                [class*="GridItemStyle--"].mfg-wrap{
                    padding: 2px 0;
                }
                .product-num-brand [class*="GridOffset"] [class*="GridItemStyle"]{
                    padding: 2px;
                }
                .product-num-brand [class*="GridOffset"] [class*="GridItemStyle"] [class*="TypographyStyle"]{
                    color: ${themeColors.colorBlack};
                    font-size: ${themeFontsSize.size12};
                    font-family: ${themeFonts.fontFamilyTTFors};
                    text-align: left;
                }
                .erVqnB{
                    display: none;
                }
                .offer-info.feature-product-title{
                    padding: 0 0;
                    text-align: left;
                }
            }
            // Brands
                .wrapper.brands-title {
                    padding: 0 16px;
                }
                .brands-container{
                    .brands-title{
                        margin-bottom: 0;
                    }
                }
                .brands-title .offer-info {
                    padding: 38px 0px;
                }
                .brands-container .offer-info{
                    padding: 2px;
                }
                .brands-container .offer-info h4{
                    padding: 40px 0 40px 20px;
                    font-family: ${themeFonts.fontFamilyPoppinsSemiBold};
                }
                .who-we-served-container h4.who-we-serve-title{
                    padding: 30px 0 24px;
                    margin: 0;
                }
            @media screen and (max-width: 1023px) {
                .who-we-served-container h4.who-we-serve-title {
                    padding: 0px 16px 24px;
                }
                .feature-product-container .product-carousel-wrapper [data-test-selector="slides"] > div {
                    width: 50%;
                    max-width: calc(50% - 10px);
                }
                .current-promotion-container .feature-product-title h4 {
                    margin-bottom: 0;
                    padding: 0 16px;
                }
                .feature-product-container .product-carousel-wrapper > div > [class*="GridItem"] {
                    max-width: 100%;
                    flex-basis: 100%;
                }
                }
                .wrapper.customer-header{
                    margin-left: 0;
                    .offer-info h4{
                        margin-bottom: 20px !important;
                        padding-top: 20px;
                        font-family: ${themeFonts.fontFamilyPoppinsSemiBold};
                    }
                    .offer-info p{
                        color: ${themeColors.colorBlack};
                        margin-bottom: 0;
                        font-family: ${themeFonts.fontFamilyTTFors};
                        line-height: 22.4px;
                    }
                }
                main div[class*="GridWrapper--"].current-promotion-container{
                    padding-bottom: 40px;
                }
                @media (min-width: 1024px){
                    .wrapper.customer-header{
                        margin-left: -22px;
                    }
                    .who-we-serve-title{
                        padding: 0;
                    }
                    main div[class*="GridWrapper--"].current-promotion-container{
                        padding-bottom: 0;
                    }
                    .current-promotion-container > div[class*="GridOffset"] {
                        gap: 60px; 
                        flex-wrap: nowrap;
                    }
                    .who-we-served-small-img p{
                        top: -36px;
                    }
                }
                @media screen and (max-width: 1023px) {
                    .feature-product-container .product-carousel-wrapper [data-test-selector="slides"] > div {
                        width: 50%;
                        max-width: calc(50% - 10px);
                    }
                    .feature-product-container .product-carousel-wrapper  {
                        padding: 0 16px;
                    }
                    .current-promotion-container .feature-product-title h4 {
                        margin-bottom: 0;
                        padding: 0 16px;
                    }
                    .feature-product-container .product-carousel-wrapper > div > [class*="GridItem"] {
                        max-width: 100%;
                        flex-basis: 100%;
                    }
                    }
                    @media screen and (max-width: 1023px) {
                        .become-a-customer-container .offer-info {
                            padding: 0;
                        }
                        .become-a-customer-container .customer-header {
                            padding: 0 16px 24px;
                        }
                        .become-a-customer-container .offer-info h4,
                        .become-a-customer-container .offer-info p {
                            padding: 0 0 16px;
                            margin: 0;
                        }
                        .become-a-customer-container > div > div:last-child > div > div {
                            float: none;
                        }
                        main div[class*="GridWrapper"].become-a-customer-container {
                            padding-bottom: 0;
                        }
                    }

                    @media screen and (min-width: 1300px) {
                        .view-items-list{
                            & > div {
                                gap: 35px;
                                & > div  {                              
                                    flex-basis: unset;
                                    max-width: unset;
                                }
                            }
                        }
                    }
                
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            main[data-test-selector="homePage"] {
                                .service-section {
                                    padding-top: 30px;
                                }
                            }
                            .gerimed-image-cell {
                                width: 100%;
                            }
                            .gerimed-custom-table-cell {
                                display: block;
                            }
                        `,
                        null,
                        css`
                            .drawer {
                                [aria-labelledby="drawerTitle"] {
                                    box-shadow: none;
                                }
                            }
                        `,
                    ],
                    "max",
                )}
            @media screen and (min-width: 768px) {
                .hide-in-desktop {
                    display: none;
                }
                .guest .my-account-link {
                    display: none;
                }
                .logged-in {
                    .become-a-customer {
                        display: none;
                    }
                    .header-wrapper {
                        margin-top: 0;
                    }
                    .nav-links .links-wrapper:last-child .list-link {
                        border-left: none;
                    }
                }
            }

            // WishList / My Order Form Start
            [data-test-selector="wishListCard"] {
                [data-test-selector="wishListCardLastUpdated"] {
                    color: ${themeColors.lightGray1};
                    font-size: ${themeFontsSize.size15};
                }

                [data-test-selector="wishListCardName"] {
                    font-weight: ${themeFontWeights.bold};
                    [class*="TypographyStyle"] {
                        font-weight: ${themeFontWeights.bold};
                    }
                }

                [wishListCardDeleteButton_f5c93499-f162-496f-bf3b-b180006b7c06] {
                    background: none;
                    color: #008cba;
                    padding: 0;
                    &:hover {
                        background: none;
                        text-decoration: underline;
                    }
                }
            }

            @media screen and (max-width: 768px) {
                [data-test-selector="wishListCard"] {
                    [class*="GridOffset"] {
                        flex-direction: column;
                        & > [class*="GridItemStyle"] {
                            flex-basis: 100%;
                            max-width: 100%;
                            [class*="HiddenStyle"] {
                                display: block !important;
                                [class*="OverflowWrapper"] {
                                    display: none;
                                }
                            }
                        }
                    }
                }
            }

            [class*="TooltipWrapper"] {
                [class*="IconWrapper"] {
                    svg {
                        fill: ${themeColors.neroColor}!important;
                        color: ${themeColors.colorWhite};
                    }
                }
            }

            .item-added {
                font-size: ${themeFontsSize.size14};
                color: ${themeColors.lightGray1};
            }

            .wishlist-line-description-grid-item {
                .availability {
                    color: ${themeColors.darkGgreen};
                    margin-bottom: 15px;
                }

                .availability-special-offer {
                    color: ${themeColors.drakYellow};
                    margin-bottom: 15px;
                }
            }

            .textBadgeWrapper {
                font-size: ${themeFontsSize.size10};
                span {
                    display: inline-block;
                    padding-left: 35px;
                    line-height: 40px;
                    margin: 10px 10px 0 0;
                }
                .search-formulary {
                    background-image: url(/userfiles/images/formulary.png) !important;
                    background-repeat: no-repeat !important;
                    color: ${themeColors.brightGreen};
                    background-position: top left;
                }
                .search-purchase-history {
                    background-image: url(/userfiles/images/purchase-history.png) !important;
                    background-repeat: no-repeat !important;
                    color: ${themeColors.heavyBlue2};
                    background-position: top left;
                }
                .search-equipment-formulary {
                    background-image: url(/userfiles/images/office-supplies.png) !important;
                    background-repeat: no-repeat !important;
                    color: ${themeColors.drakYellow};
                    background-position: top left;
                }

                .search-medical-equipment {
                    background-image: url(/userfiles/images/medical-equipment.png) !important;
                    background-repeat: no-repeat !important;
                    color: ${themeColors.colorMed};
                    background-position: top left;
                }
            }
            [data-test-selector="cardListProductsNarrow"],
            [data-test-selector="productListCards_Table"],
            [data-test-selector="productListCards_List"],
            .product-carousel-wrapper,
            [data-test-selector="productListCards_Grid"] {
                .product-num-brand  {
                                    > [class*="GridOffset"] {
                                                    gap: 10px;
                                                    flex-wrap: wrap;
                                                        > div {
                                                            margin: 0;
                                                        }
                                                    }
                                    }
            }
            .product-carousel-wrapper{
                .product-num-brand  {
                    > [class*="GridOffset"] {
                        margin: 0;
                        width: 100%;
                        }
                    }
            }
            [data-test-selector="productListCards_Grid"] {
                .product-num-brand {
                    > [class*="GridOffset"] {
                        margin: 0;
                        width: 100%;
                    }
                }
            }

            .partNumbersHidden,
            .product-num-brand {
                .partNumbersGridItem {
                    padding: 10px 2px;
                }
                [class*="GridOffset"] {
                    display: flex !important;
                    flex-direction: row;
                    flex-wrap: nowrap;
                    align-items: center;
                    [class*="GridItemStyle"] {
                        display: flex !important;
                        flex-direction: row;
                        flex-basis: auto;
                        max-width: auto;
                        padding: 0;
                        [class*="TypographyStyle"] {
                            color: ${themeColors.lightGray1};
                            font-size: ${themeFontsSize.size14};
                            font-weight: ${themeFontWeights.medium};
                            line-height: ${themeFontsSize.size14};
                        }
                    }
                }
            }

            .product-slider .product-num-brand {
                [class*="GridOffset"] {
                    [class*="GridItemStyle"] {
                        [class*="TypographyStyle"] {
                            color: ${themeColors.lightBlack};
                            font-size: ${themeFontsSize.size10};
                            font-weight: ${themeFontWeights.regular};
                            line-height: ${themeFontsSize.size12};
                        }
                    }
                }
            }   
            .modal-wrap {
                [class*="FormFieldStyle"] {
                    [data-test-selector="myLists_editNote"] {
                        max-height: 250px;
                        min-height: 100px;
                        width: 100% !important;
                    }
                    [class*="DescriptionWrapper"] {
                        background: none;
                        .form-field-error {
                            color: ${themeColors.errorRed};
                            padding: 0;
                        }
                    }
                }
            }

            [data-test-selector="scheduleReminderModal"] {
                [class*="ModalTitle"] {
                    padding: 10px 20px 0;
                }
                [data-test-selector="modal_ModalContent"] {
                    padding: 0 20px 20px;
                }
                [class*="RadioGroupStyle"] {
                    input[type="radio"] {
                        position: absolute;
                    }
                    [class*="TypographyStyle"] {
                        margin: 0;
                    }
                }
                [class*="ModalContent"] {
                    [class*="GridWrapper"] {
                        & > [class*="GridOffset"] {
                            width: 100% !important;
                            margin: 0;
                            & > [class*="GridItemStyle"] {
                                padding: 0 5px 5px 0 !important;
                            }
                        }
                        [class*="FormInputWrapper"] {
                            margin-top: 0;
                        }
                    }
                }
            }

            // Request Reports
            .request-reports {
                max-width: 60%;
                margin: 0;
                [class*="GridItemStyle"] {
                    padding: 0;
                }
                [class*="GridOffset"] {
                    width: auto;
                    margin: 0;
                    padding: 0;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                                max-width: 100%;
                                [class*="GridItemStyle"],
                                [class*="ContentWrapper"] {
                                    padding: 0 15px;
                                }
                            `,
                    ],
                    "max",
                )}
            }

            [class*="ModalContent"] {
                h5 {
                    margin-bottom: 0;
                }
            }
            [class*="DataTableStyle"] {
                border: 1px solid ${themeColors.lightGray8};
                [class*="DataTableBodyStyle"] {
                    [class*="DataTableRowStyle"] {
                        background: ${themeColors.colorDisabled};
                        &:nth-child(even) {
                            background: ${themeColors.colorWhite};
                        }
                    }
                }
            }

            [data-test-selector="userListHeader"] {
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                                [class*="GridItemStyle"] {
                                    max-width: 100%;
                                    flex-basis: 100%;
                                }
                            `,
                    ],
                    "max",
                )}
            }

            // Dashboard
            .dashboard-openorder {
                padding: 0;
                .dashboard-datatable {
                    overflow-x: auto;
                }
                [class*="GridOffset"] {
                    margin: 0;
                    padding: 0;
                }
                [class*="GridItemStyle"] {
                    padding: 0;
                }
                h2 {
                    margin-top: 0;
                }
            }

            [data-test-selector="myAccount"] {
                // [class*="GridOffset"] {
                //     margin-bottom: 25px;
                // }
                [class*="Wrapper"] {
                    h2 {
                        font-size: ${themeFontsSize.size17};
                        font-weight: ${themeFontWeights.fontWeightForBold};
                        color: ${themeColors.colorNewBlack};
                    }
                }
                .user-lists {
                    margin-top: 25px;
                }
                .navigation-list {
                    li {
                        margin-bottom: 5px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                                [class*="GridOffset"] {
                                    margin-bottom: 0;
                                }
                            `,
                    ],
                    "max",
                )}
            }        
            [data-test-selector*="page_MyAccountPage"] {
                a:hover{
                    text-decoration: underline;
                }
            }       

            .dashboard-footer-links {
                border-top: 1px solid ${themeColors.lightGray};
                border-bottom: 1px solid ${themeColors.lightGray};
                margin: 20px 0 25px;
                padding: 25px 0;
                h4 {
                    font-weight: ${themeFontWeights.fontWeightForBold};
                    font-size: ${themeFontsSize.size17};
                    margin-bottom: 17px;
                }
                ul {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }
            }

            .dashboard-footer-widget {
                border: 1px solid ${themeColors.lightGray};
                padding: 0 0 25px;
                max-width: 50%;
                width: 50%;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                                max-width: 100%;
                                width: 100%;
                            `,
                        null,
                        null,
                    ],
                    "max",
                )}
                ul {
                    padding: 0;
                    margin: 0;
                    list-style: none;
                    text-align: center;
                    display: table;
                    width: 100%;
                    li {
                        display: table-cell;
                    }
                    width: 100%;
                    img {
                        max-width: 150px;
                    }
                }
                strong {
                    background: ${themeColors.lightGray20};
                    display: block;
                    text-align: center;
                    font-weight: ${themeFontWeights.fontWeightForBold};
                    font-size: ${themeFontsSize.size14};
                    padding: 5px;
                }
            }

            .dashboard-pannel {
                display: flex;
                flex-direction: row;
                gap: 20px;
                margin-bottom: 25px;
                li {
                    position: relative;
                    width: 30%;
                    align-items: center;
                    text-align: center;
                    a {
                        background: ${themeColors.colorTertiary};
                        color: ${themeColors.colorWhite};
                        font-size: ${themeFontsSize.size18};
                        text-transform: uppercase;
                        font-weight: ${themeFontWeights.medium};
                        padding: 60px 0 15px;
                        border-radius: 4px;
                        height: 100%;
                        align-items: center;
                        display: flex;
                        justify-content: space-around;
                        flex-direction: column;
                        span {
                            display: flex;
                        }
                        &:hover {
                            background: ${themeColors.dullBlue};
                            color: ${themeColors.colorWhite}!important;
                            span {
                                color: ${themeColors.colorWhite}!important;
                            }
                        }
                    }
                    label {
                        position: absolute;
                        top: 5px;
                        left: 10px;
                        color: ${themeColors.colorWhite};
                        font-size: ${themeFontsSize.size20};
                        font-weight: ${themeFontWeights.fontWeightForBold};
                    }
                    .pannel-icon {
                        display: flex;
                        text-align: center;
                        margin-bottom: 25px;
                        svg {
                            color: ${themeColors.colorWhite};
                            font-size: ${themeFontsSize.size44};
                        }
                    }
                    &.db-li-oapp {
                        .req-icon {
                            display: none;
                        }
                    }
                    &.db-li-req {
                        .oapp-icon {
                            display: none;
                        }
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                    max-width: 100%;
                                    width: 100%;
                                `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
                }
            }
            .billToSelector {
                margin-bottom: 20px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            .breadcrumb-wrap {
                                margin: 0 !important;
                            }
                        `,
                    ],
                    "max",
                )}

            // Order History
            .react-datetime-picker__inputGroup {
                display: flex;
                flex-direction: row;
                align-items: center;
                .react-datetime-picker__inputGroup__divider {
                    display: flex;
                    align-items: center;
                }
            }
            .react-datetime-picker__inputGroup__leadingZero {
                display: flex;
                align-items: center;
                margin: 0 -2px 0 2px;
            }
            [data-test-selector="orderHistory_filterDateRange"] {
                & > div {
                    width: 50%;
                }
            }

            [data-test-selector="orderDetails_orderNumber"] {
                span {
                    display: block;
                    margin-bottom: 10px;
                }
            }

            .order-details-info {
                padding: 15px;
                [class*="Wrapper"] {
                    h3,
                    h2 {
                        display: inline-block;
                        margin-right: 10px;
                        padding-top: 0;
                        font-size: 17px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                [class*="GridOffset"] {
                                    [class*="GridItemStyle"] {
                                        max-width: 100%;
                                        flex-basis: 100%;
                                        padding-bottom: 15px;
                                        [data-test-selector="orderDetailBillingInformation"] {
                                            padding-bottom: 0;
                                        }
                                    }
                                }
                            `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
            }

            [data-test-selector="orderDetailShippingInformation"] {
                padding: 0 20px 0 0;
            }
            .card_container__wrapper {
                    [class*="ButtonWrapper"] {
                        white-space: nowrap;
                    }
                        [data-test-selector="productDescriptionLink"] {
                            font-weight:${themeFontWeights.fontWeightForBold};
                        }
                    [data-test-selector="heading"] {
                            font-weight:${themeFontWeights.fontWeightForBold};
                            font-size:${themeFontsSize.size12};
                        & + [class*="TypographyStyle"] {
                            text-align:left;
                            font-size:${themeFontsSize.size16};
                        }
                    }                    
                }

            .order-details-grid {
                [class*="GridOffset"] {
                    [class*="GridWrapper"] {
                        background: none;
                        border: 1px solid ${themeColors.orderBorderColor};
                        padding: 15px;
                    }
                }
            }

            .unit-measure-section {
                [data-test-selector="product_unitOfMeasureSelect-label"] {
                    font-size: ${themeFontsSize.size15};
                }
            }

            [data-test-selector="page_ProductDetailsPage"] {
                .unit-measure-section {
                    [data-test-selector="product_unitOfMeasureSelect-label"] {
                        & + [class*="FormInputWrapper"] {
                            max-width: 66.66667%;
                            @media only screen and (min-width: 767px) {
                                width: fit-content;
                            }
                        }
                    }
                }
            }

            [data-test-selector="page_ProductListPage"] {
                .unit-measure-section {
                    text-align: right;

                    > [class*="FormFieldStyle"] {
                        flex-direction: row;
                        align-items: center;
                        justify-content: flex-end;
                        column-gap: 10px;

                        @media only screen and (max-width: 768px) {
                            justify-content: flex-start;
                        }
                    }

                    [data-test-selector="product_unitOfMeasureSelect-label"] {
                        width: auto;
                        & + [class*="FormInputWrapper"] {
                            margin-top: 0;
                            width: auto;
                            min-width: 100px;
                        }
                    }
                }

                [data-test-selector="productList_pagination"] {
                    .page-per-select {
                        display: none;
                    }
                }
            }

            [data-test-selector="page_MyListsDetailsPage"] {
                .unit-measure-section {

                    > [class*="FormFieldStyle"] {
                        flex-direction: row;
                        align-items: center;
                        column-gap: 10px;
                    }

                    [data-test-selector="product_unitOfMeasureSelect-label"] {
                        width: auto;
                        & + [class*="FormInputWrapper"] {
                            margin-top: 0;
                            width: auto;
                            min-width: 85px;
                        }
                    }
                }
            }

            .recap-code-title {
                margin-bottom: 17px;
                margin-top: 16px;
            }

            [data-test-selector="quickViewModal"] {
                [data-test-selector="modal_ModalContent"] {
                    height: 450px;

                    @media only screen and (max-height: 480px) {
                        height: 320px !important;
                        min-height: 320px !important;
                    }
                }
            }

            /* Website Tutorials Grid */
            .website-tutorial-row {
                > ${GridOffset} {
                    width: 100%;
                    margin: 0;

                    > ${GridItemStyle} {
                        padding-top: 0;
                    }

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            null,
                            css`
                                > ${GridItemStyle} {
                                    flex-basis: 100%;
                                    max-width: 100%;
                                    padding-bottom: 5px;
                                }
                            `,
                            null,
                            null,
                        ], "max")}
                }
            }
            body .container {
                .top-menu [class*="TypographyStyle"]{
                    font-family: ${themeFonts.fontFamilyLexendDeca};
                }
            } 
            .login-form input:not(.react-datetime-picker__inputGroup__input),
            .login-form input:not(.react-datetime-picker__inputGroup__input):focus{
                background-color: ${themeColors.colorLightBlue};
            }
            .become-a-customer-container{
                border-bottom: 1px solid ${themeColors.colorLightGrey};
            }
            .current-promotion-container{
                // border-bottom: 1px solid ${themeColors.colorLightGrey};
                // border-top: 1px solid ${themeColors.colorLightGrey};
                .offer-info h4{
                    font-family: ${themeFonts.fontFamilyPoppinsSemiBold};
                }
                .text-sub-heading{
                    font-family: ${themeFonts.fontFamilyTTFors};
                }
            }
            .stayInformedSection{
                border-top: 1px solid ${themeColors.colorLightGrey};
            }
            .whoServedSection,
            .current-promotion-container,
            .products-wrap,
            .stayInformedSection,
            .become-a-customer-container {
                > div {
                    max-width: 1180px;  
                    margin: 0 auto;
                }
                button{
                    font-family: ${themeFonts.fontFamilyPoppinsMedium};
                }
            }
            @media only screen and (min-width: 601px) and (max-width: 1024px) {
                .current-promotion-container.feature-products-container {
                    padding-top: 0;
                }
                .current-promotion-container .slideshow-slide-container .slide-wrapper{
                    min-height: 250px;
                }
            }
                
            main[data-test-selector="homePage"] [class*="GridWrapper"].current-promotion-container{
                padding-top: 40px;
            }
            .become-a-customer-container {
                .img-wrapper {
                    padding: 0;
                    display: flex;
                    @media only screen and (min-width: 1024px) {
                        img {
                            height: 358px;
                        }
                    }                    
                }
                > div {
                    > div {
                        align-items: center;
                        .offer-info {
                            padding-top: 0;
                        }
                    }
                }
                border-bottom: none;
            }            
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                null,
                css`
                    .current-promotion-container {
                        > div {
                            > div {
                                padding: 0 16px;
                            }
                        }
                    }
                    .stayInformedSection,
                    .product-wrap {
                        > div {
                            > div {
                                padding: 0 16px;
                            }
                        }
                    }
                    .testimonial-card {
                        width: 100%;
                    }
                `,
            ], "max")}
        `,
    },
});
